import { Box, IconButton, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useAuth } from "../../context/AuthContext";
import {
  ADMIN_USER_TYPE,
  EMAIL_USER_TYPE,
  DASHBOARD_V2_TOP_NAVBAR_HEIGHT,
} from "../../utils/constants";
import SingleFieldSelector from "../SingleFieldSelector";
import { Breadcrumb } from "../../primitive-components";
import { HeaderActionsSlot } from "./HeaderActions";
import BrandSwitcher from "./BrandSwitcher";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { CATEGORY, PAGE, SCREEN, triggerGa } from "../../utils/gaUtils";

const trackTopBar = (action, extra = {}) =>
  triggerGa({
    category: CATEGORY.NAVIGATION,
    action,
    page: window.location.pathname.split("/").filter(Boolean)[0] || PAGE.HOME,
    screenName: SCREEN.TOP_BAR,
    ...extra,
  });

// Breadcrumbs on the left; on the right the brand switcher (admins get two selectors, email users
// one dropdown). The profile is reached from the sidebar.
export default function TopNavbar() {
  const breadcrumbs = useBreadcrumbs();
  const navigate = useNavigate();
  const {
    user,
    brandList,
    selectedBrand,
    handleBrandChangeByAdmin,
    onboardingBrandList,
    selectedOnboardingBrand,
    handleOnboardingBrandChangeByAdmin,
    selectedEmailUserBrand,
    handleBrandChangeByEmailUser,
  } = useAuth();

  const isAdminUser = user?.data?.user_type === ADMIN_USER_TYPE;
  const isEmailUser = user?.data?.user_type === EMAIL_USER_TYPE;
  const brandListData = (brandList?.list || []).map((value) => ({
    id: value.id,
    label: value.display_name ? value.display_name : value.name,
    value: value.display_name ? value.display_name : value.name,
  }));
  const onboardingBrandListData = (onboardingBrandList?.list || [])
    .filter((value) => !value.finalized_brand_id)
    .map((value) => ({
      id: value.id,
      label: value.display_name,
      value: value.display_name,
    }));
  const accessibleBrandListData = (user?.data?.accessible_brands || []).map(
    (value) => ({
      id: value.id,
      label: value.display_name || value.name,
      value: value.display_name || value.name,
    }),
  );
  const currentEmailUserBrand =
    accessibleBrandListData.find(
      (brand) => brand.id === selectedEmailUserBrand?.id,
    ) ||
    accessibleBrandListData.find(
      (brand) => brand.id === user?.data?.parent_brand?.id,
    ) ||
    null;

  return (
    <Box
      sx={{
        height: DASHBOARD_V2_TOP_NAVBAR_HEIGHT,
        pl: "20px",
        pr: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: "#FFFFFF",
        zIndex: 50,
        position: "sticky",
        top: 0,
      }}
    >
      <Breadcrumb
        items={breadcrumbs.map((crumb) =>
          crumb.onClick
            ? {
                ...crumb,
                onClick: () => {
                  trackTopBar("breadcrumb_click", { label: crumb.label });
                  crumb.onClick();
                },
              }
            : crumb,
        )}
        sx={{ padding: "12px 0", minWidth: 0, flex: "1 1 0", mr: 3 }}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <HeaderActionsSlot />
        {isAdminUser ? (
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box sx={{ width: 360, flexShrink: 0 }}>
              <SingleFieldSelector
                height={30}
                name="DIY Onboarding Brands"
                fields={onboardingBrandListData}
                selectedValue={selectedOnboardingBrand}
                onSelectionChange={handleOnboardingBrandChangeByAdmin}
                placeholder="SELECT BRAND"
                margin={0.7}
                width="100%"
                labelPosition="left"
              />
            </Box>
            <Box sx={{ width: 320, flexShrink: 0 }}>
              <SingleFieldSelector
                height={30}
                name="Brands"
                fields={brandListData}
                selectedValue={selectedBrand}
                onSelectionChange={handleBrandChangeByAdmin}
                placeholder="SELECT BRAND"
                margin={0.7}
                width="100%"
                labelPosition="left"
              />
            </Box>
          </Stack>
        ) : null}
        {isEmailUser && accessibleBrandListData.length ? (
          <BrandSwitcher
            brands={accessibleBrandListData}
            current={currentEmailUserBrand}
            onOpen={() => trackTopBar("brand_switcher_open")}
            onChange={(brand) => {
              trackTopBar("brand_switched", {
                label: String(currentEmailUserBrand?.id ?? ""),
                label2: String(brand.id),
              });
              handleBrandChangeByEmailUser(brand);
            }}
          />
        ) : null}
        {/* The bell opens Announcements */}
        <IconButton
          aria-label="Announcements"
          onClick={() => {
            trackTopBar("announcements_bell_click");
            navigate("/announcements");
          }}
          sx={{ width: 40, height: 40 }}
        >
          <NotificationsNoneIcon sx={{ fontSize: 24, color: "#000000" }} />
        </IconButton>
      </Stack>
    </Box>
  );
}
